/* ============================================================
   HFO - PRODUCT PAGE SIBLINGS V4 FAST

   PERFORMANCE:
   - Restore PDP sibling HTML from localStorage immediately
   - Read shared winner metadata discovered on collections/search
   - Request the known winning Metaobject page FIRST
   - Display candidate as soon as response returns
   - Verify remaining pages in parallel
   - Higher/newer Metaobject ID always wins
   - 6 hour cache
   ============================================================ */

(function () {
  'use strict';


  if (
    !/\/products\/[^/]+\/?$/i.test(
      window.location.pathname
    )
  ) {
    return;
  }


  if (
    window.HFOProductSiblingFastV4Installed
  ) {
    return;
  }

  window.HFOProductSiblingFastV4Installed =
    true;



  /* ==========================================================
     SETTINGS
     ========================================================== */

  var CACHE_TTL =
    6 * 60 * 60 * 1000;


  var PDP_CACHE_PREFIX =
    'hfoSiblingPdpV4:';


  var WINNER_CACHE_PREFIX =
    'hfoSiblingWinnerV2:';


  var running =
    Object.create(null);



  /* ==========================================================
     HELPERS
     ========================================================== */

  function cssEscape(value) {
    value = String(value);

    if (
      window.CSS &&
      typeof window.CSS.escape ===
      'function'
    ) {
      return window.CSS.escape(value);
    }

    return value.replace(
      /[^a-zA-Z0-9_-]/g,
      '\\$&'
    );
  }


  function normalizeEntryId(value) {
    value =
      String(value || '0')
        .replace(/\D/g, '')
        .replace(/^0+/, '');

    return value || '0';
  }


  function compareEntryIds(
    a,
    b
  ) {
    a = normalizeEntryId(a);
    b = normalizeEntryId(b);

    if (a.length > b.length) {
      return 1;
    }

    if (a.length < b.length) {
      return -1;
    }

    if (a === b) {
      return 0;
    }

    return a > b ? 1 : -1;
  }


  function now() {
    return Date.now();
  }



  /* ==========================================================
     LOCAL STORAGE
     ========================================================== */

  function readLocal(key) {
    try {
      var raw =
        localStorage.getItem(key);

      if (!raw) {
        return null;
      }

      var data =
        JSON.parse(raw);

      if (
        !data ||
        !data.savedAt ||
        (
          now() - data.savedAt >
          CACHE_TTL
        )
      ) {
        localStorage.removeItem(key);
        return null;
      }

      return data;

    } catch (error) {
      return null;
    }
  }


  function writeLocal(
    key,
    data
  ) {
    try {
      data.savedAt = now();

      localStorage.setItem(
        key,
        JSON.stringify(data)
      );
    } catch (error) {
      /*
       * Ignore localStorage errors.
       */
    }
  }


  function getPdpCache(
    productId
  ) {
    return readLocal(
      PDP_CACHE_PREFIX +
      String(productId)
    );
  }


  function setPdpCache(
    productId,
    result
  ) {
    if (!result) {
      return;
    }

    writeLocal(
      PDP_CACHE_PREFIX +
      String(productId),
      {
        entryId:
          normalizeEntryId(
            result.getAttribute(
              'data-sibling-entry-id'
            ) || '0'
          ),

        html:
          result.outerHTML
      }
    );
  }


  function getWinnerMeta(
    productId
  ) {
    return readLocal(
      WINNER_CACHE_PREFIX +
      String(productId)
    );
  }


  function setWinnerMeta(
    productId,
    entryId,
    page,
    count,
    handle
  ) {
    writeLocal(
      WINNER_CACHE_PREFIX +
      String(productId),
      {
        entryId:
          normalizeEntryId(
            entryId
          ),

        page:
          Number(page || 1),

        count:
          Number(count || 0),

        handle:
          String(handle || '')
      }
    );
  }



  /* ==========================================================
     DOM
     ========================================================== */

  function getHost(productId) {
    return document.querySelector(
      '[data-hfo-product-sibling-host]' +
      '[data-product-id="' +
      cssEscape(
        String(productId)
      ) +
      '"]'
    );
  }


  function getCurrentResult(host) {
    if (!host) {
      return null;
    }

    return host.querySelector(
      '[data-hfo-product-sibling-result]'
    );
  }


  function getEntryId(result) {
    if (!result) {
      return '0';
    }

    return normalizeEntryId(
      result.getAttribute(
        'data-sibling-entry-id'
      ) || '0'
    );
  }



  /* ==========================================================
     INSERT WINNER
     ========================================================== */

  function insertWinner(
    productId,
    sourceResult,
    pageNumber
  ) {
    var host =
      getHost(productId);

    if (
      !host ||
      !sourceResult
    ) {
      return false;
    }


    var candidateId =
      getEntryId(
        sourceResult
      );


    var current =
      getCurrentResult(host);


    var currentId =
      current
        ? getEntryId(current)
        : normalizeEntryId(
            host.getAttribute(
              'data-hfo-selected-sibling-entry-id'
            ) || '0'
          );


    if (
      current &&
      currentId !== '0' &&
      compareEntryIds(
        candidateId,
        currentId
      ) <= 0
    ) {
      return false;
    }


    var imported =
      document.importNode(
        sourceResult,
        true
      );


    if (
      current &&
      current.parentNode === host
    ) {
      host.replaceChild(
        imported,
        current
      );
    } else {
      var placeholder =
        host.querySelector(
          '[data-hfo-product-sibling-placeholder]'
        );

      if (placeholder) {
        host.insertBefore(
          imported,
          placeholder
        );
      } else {
        host.appendChild(
          imported
        );
      }
    }


    host.setAttribute(
      'data-hfo-selected-sibling-entry-id',
      candidateId
    );


    var liveResult =
      getCurrentResult(host);


    if (liveResult) {
      setPdpCache(
        productId,
        liveResult
      );
    }


    setWinnerMeta(
      productId,
      candidateId,
      pageNumber || 1,
      parseInt(
        sourceResult.getAttribute(
          'data-sibling-count'
        ) || '0',
        10
      ) || 0,
      sourceResult.getAttribute(
        'data-sibling-entry-handle'
      ) || ''
    );


    document.dispatchEvent(
      new CustomEvent(
        'hfo:product-siblings:loaded',
        {
          detail: {
            productId:
              String(productId),

            entryId:
              candidateId,

            page:
              pageNumber || 1
          }
        }
      )
    );


    return true;
  }



  /* ==========================================================
     RESTORE PDP CACHE IMMEDIATELY
     ========================================================== */

  function restorePdpCache(
    productId
  ) {
    var cached =
      getPdpCache(productId);

    if (
      !cached ||
      !cached.html
    ) {
      return false;
    }


    var doc =
      new DOMParser()
        .parseFromString(
          cached.html,
          'text/html'
        );


    var result =
      doc.querySelector(
        '[data-hfo-product-sibling-result]'
      );


    if (!result) {
      return false;
    }


    return insertWinner(
      productId,
      result,
      0
    );
  }



  /* ==========================================================
     PARSE SECTION RESPONSE
     ========================================================== */

  function resultFromHTML(
    html,
    productId
  ) {
    if (!html) {
      return null;
    }


    var doc =
      new DOMParser()
        .parseFromString(
          html,
          'text/html'
        );


    return (
      doc.querySelector(
        '[data-hfo-product-sibling-result]' +
        '[data-product-id="' +
        cssEscape(
          String(productId)
        ) +
        '"]'
      ) ||
      doc.querySelector(
        '[data-hfo-product-sibling-result]'
      )
    );
  }



  /* ==========================================================
     REQUEST ONE METAOBJECT PAGE
     ========================================================== */

  function fetchSectionPage(
    sectionId,
    pageParam,
    pageNumber
  ) {
    var url =
      new URL(
        window.location.href
      );


    url.searchParams.set(
      pageParam,
      String(pageNumber)
    );


    url.searchParams.set(
      'section_id',
      sectionId
    );


    url.searchParams.set(
      '_hfo_pdp_fast',
      '4'
    );


    return fetch(
      url.toString(),
      {
        method: 'GET',

        credentials:
          'same-origin',

        headers: {
          'X-Requested-With':
            'XMLHttpRequest'
        }
      }
    )

      .then(
        function (response) {
          if (!response.ok) {
            throw new Error(
              'PDP sibling lookup failed: ' +
              response.status
            );
          }

          return response.text();
        }
      );
  }



  /* ==========================================================
     PROCESS ONE PAGE IMMEDIATELY
     ========================================================== */

  function requestAndProcessPage(
    productId,
    sectionId,
    pageParam,
    pageNumber
  ) {
    return fetchSectionPage(
      sectionId,
      pageParam,
      pageNumber
    )

      .then(
        function (html) {
          var candidate =
            resultFromHTML(
              html,
              productId
            );

          if (!candidate) {
            return null;
          }


          /*
           * Do not wait for other pages.
           * Insert this page immediately if newer.
           */
          insertWinner(
            productId,
            candidate,
            pageNumber
          );


          return candidate;
        }
      )

      .catch(
        function (error) {
          console.warn(
            'PDP sibling page lookup failed:',
            error
          );

          return null;
        }
      );
  }



  /* ==========================================================
     VERIFY ALL METAOBJECT PAGES
     ========================================================== */

  function scanPlaceholder(
    placeholder
  ) {
    if (
      !placeholder ||
      !document.documentElement.contains(
        placeholder
      )
    ) {
      return Promise.resolve();
    }


    var productId =
      String(
        placeholder.getAttribute(
          'data-product-id'
        ) || ''
      );


    var sectionId =
      String(
        placeholder.getAttribute(
          'data-section-id'
        ) || ''
      );


    var pageParam =
      String(
        placeholder.getAttribute(
          'data-page-param'
        ) || ''
      );


    var totalPages =
      parseInt(
        placeholder.getAttribute(
          'data-total-pages'
        ) || '1',
        10
      );


    if (
      !productId ||
      !sectionId ||
      !pageParam ||
      totalPages <= 1
    ) {
      return Promise.resolve();
    }


    var key =
      productId +
      '|' +
      sectionId +
      '|' +
      pageParam;


    if (running[key]) {
      return running[key];
    }


    /*
     * Restore exact PDP markup first.
     */
    restorePdpCache(
      productId
    );


    /*
     * Winner metadata may already have been discovered
     * on a Collection/Search/Recommendation page.
     */
    var winnerMeta =
      getWinnerMeta(productId);


    var preferredPage = 0;


    if (
      winnerMeta &&
      winnerMeta.page &&
      winnerMeta.page > 1 &&
      winnerMeta.page <= totalPages
    ) {
      preferredPage =
        Number(winnerMeta.page);
    }


    var requests = [];


    /*
     * Request known winning page FIRST.
     */
    if (preferredPage > 1) {
      requests.push(
        requestAndProcessPage(
          productId,
          sectionId,
          pageParam,
          preferredPage
        )
      );
    }


    /*
     * Then verify every remaining page.
     */
    for (
      var page = 2;
      page <= totalPages;
      page++
    ) {
      if (
        page === preferredPage
      ) {
        continue;
      }

      requests.push(
        requestAndProcessPage(
          productId,
          sectionId,
          pageParam,
          page
        )
      );
    }


    running[key] =
      Promise.all(requests)

        .then(
          function () {
            var host =
              getHost(productId);

            var current =
              getCurrentResult(host);


            if (current) {
              setPdpCache(
                productId,
                current
              );


              /*
               * If result remained initial Liquid page 1,
               * save page 1 as winner.
               */
              var currentId =
                getEntryId(current);


              var storedWinner =
                getWinnerMeta(
                  productId
                );


              if (
                !storedWinner ||
                compareEntryIds(
                  currentId,
                  storedWinner.entryId
                ) >= 0
              ) {
                setWinnerMeta(
                  productId,
                  currentId,
                  storedWinner &&
                  storedWinner.entryId ===
                    currentId
                    ? storedWinner.page
                    : 1,
                  parseInt(
                    current.getAttribute(
                      'data-sibling-count'
                    ) || '0',
                    10
                  ) || 0,
                  current.getAttribute(
                    'data-sibling-entry-handle'
                  ) || ''
                );
              }
            }


            var livePlaceholder =
              document.querySelector(
                '[data-hfo-product-sibling-placeholder]' +
                '[data-product-id="' +
                cssEscape(productId) +
                '"]'
              );


            if (
              livePlaceholder &&
              livePlaceholder.parentNode
            ) {
              livePlaceholder
                .parentNode
                .removeChild(
                  livePlaceholder
                );
            }


            if (host) {
              host.setAttribute(
                'data-hfo-product-sibling-verified',
                'true'
              );
            }
          }
        )

        .finally(
          function () {
            delete running[key];
          }
        );


    return running[key];
  }



  /* ==========================================================
     SCAN ALL
     ========================================================== */

  function scanAll() {
    var placeholders =
      document.querySelectorAll(
        '[data-hfo-product-sibling-placeholder]'
      );


    for (
      var i = 0;
      i < placeholders.length;
      i++
    ) {
      scanPlaceholder(
        placeholders[i]
      );
    }
  }



  window.HFORefreshProductSiblings =
    scanAll;



  /* ==========================================================
     INITIAL START
     ========================================================== */

  /*
   * Restore cache before DOMContentLoaded if possible.
   */
  var immediateHost =
    document.querySelector(
      '[data-hfo-product-sibling-host][data-product-id]'
    );


  if (immediateHost) {
    var immediateProductId =
      immediateHost.getAttribute(
        'data-product-id'
      );

    if (immediateProductId) {
      restorePdpCache(
        immediateProductId
      );
    }
  }


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      scanAll,
      {
        once: true
      }
    );
  } else {
    scanAll();
  }


  setTimeout(
    scanAll,
    100
  );


  setTimeout(
    scanAll,
    400
  );



  /* ==========================================================
     MUTATION OBSERVER
     ========================================================== */

  var observer =
    new MutationObserver(
      function (mutations) {
        var shouldScan = false;


        for (
          var i = 0;
          i < mutations.length;
          i++
        ) {
          var nodes =
            mutations[i].addedNodes;


          for (
            var n = 0;
            n < nodes.length;
            n++
          ) {
            var node =
              nodes[n];


            if (
              !node ||
              node.nodeType !== 1
            ) {
              continue;
            }


            if (
              node.matches &&
              node.matches(
                '[data-hfo-product-sibling-placeholder]'
              )
            ) {
              shouldScan = true;
              break;
            }


            if (
              node.querySelector &&
              node.querySelector(
                '[data-hfo-product-sibling-placeholder]'
              )
            ) {
              shouldScan = true;
              break;
            }
          }


          if (shouldScan) {
            break;
          }
        }


        if (shouldScan) {
          setTimeout(
            scanAll,
            20
          );
        }
      }
    );


  observer.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );


  window.addEventListener(
    'pageshow',

    function () {
      var host =
        document.querySelector(
          '[data-hfo-product-sibling-host][data-product-id]'
        );

      if (host) {
        var productId =
          host.getAttribute(
            'data-product-id'
          );

        if (productId) {
          restorePdpCache(
            productId
          );
        }
      }

      setTimeout(
        scanAll,
        20
      );
    }
  );

})();